import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReceiptLong, Done } from '@mui/icons-material';
import api from '../services/api';
import InvoiceDialog from '../components/invoicing/InvoiceDialog';
import { formatDay, formatMoney } from '../components/commercial/contact';

type Row = { id: number; reference: string; createdAt: string; customerId?: number | null; customerName?: string | null; total: number; paymentStatus?: string | null; channel?: string | null };

/** Ventas sin factura (antes: columna FACTURA = HACER en la planilla). */
const UninvoicedPage: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [invoiceIds, setInvoiceIds] = useState<number[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.getUninvoiced());
      setSelected([]);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => rows.reduce((s, r) => s + r.total, 0), [rows]);
  const toggle = (id: number) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  // Facturar varias juntas solo si son del mismo cliente.
  const sameCustomer = selected.length > 0 && new Set(rows.filter(r => selected.includes(r.id)).map(r => r.customerId ?? r.customerName)).size === 1;

  const markManual = async (id: number) => {
    await api.markInvoiced(id);
    load();
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Facturas pendientes</Typography>
          <Typography variant="body2" color="text.secondary">
            {rows.length} ventas sin facturar · {formatMoney(total)}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<ReceiptLong />} disabled={!sameCustomer} onClick={() => setInvoiceIds(selected)}>
          Facturar {selected.length > 1 ? `${selected.length} ventas` : 'seleccionada'}
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {selected.length > 0 && !sameCustomer && <Alert severity="info" sx={{ mb: 2 }}>Para facturar juntas, elegí ventas del mismo cliente.</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Fecha</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Venta</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox"><Checkbox checked={selected.includes(r.id)} onChange={() => toggle(r.id)} /></TableCell>
                    <TableCell>{formatDay(r.createdAt)}</TableCell>
                    <TableCell>{r.customerName || 'Consumidor final'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{r.reference}{r.channel ? ` · ${r.channel}` : ''}</TableCell>
                    <TableCell align="right">{formatMoney(r.total)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="outlined" onClick={() => setInvoiceIds([r.id])}>Facturar</Button>
                        <Button size="small" startIcon={<Done />} onClick={() => markManual(r.id)}>Ya facturada</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No hay ventas sin facturar.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      <InvoiceDialog
        open={!!invoiceIds}
        orderIds={invoiceIds || []}
        onClose={() => setInvoiceIds(null)}
        onEmitted={() => { setInvoiceIds(null); load(); }}
      />
    </Box>
  );
};

export default UninvoicedPage;
